export const STATUSES = ['todo', 'in_progress', 'blocked', 'done'] as const;
export type Status = (typeof STATUSES)[number];

export const STATUS_LABELS: Record<Status, string> = {
  todo: 'To do',
  in_progress: 'In progress',
  blocked: 'Blocked',
  done: 'Done',
};

export type ProviderId =
  | 'azure_devops'
  | 'monday'
  | 'jira'
  | 'zendesk'
  | 'clickup'
  | 'liveagent'
  | 'custom_rest'
  | 'smartsheet'
  | 'basecamp'
  | 'demo';
export type Level = 1 | 2 | 3 | 4;
export type Effort = 'quick' | 'short' | 'medium' | 'large';
export type ImpactType =
  | 'revenue'
  | 'customer'
  | 'project'
  | 'team'
  | 'employee'
  | 'compliance'
  | 'internal';
export type LinkType = 'blocks' | 'relates_to' | 'duplicates';
export type ValueSource = 'user' | 'derived';
export type FactorKey =
  | 'urgency'
  | 'importance'
  | 'dependency'
  | 'impact'
  | 'commitment'
  | 'age'
  | 'effort'
  | 'alignment';
export type RolePreset = 'balanced' | 'ic' | 'manager' | 'exec' | 'support' | 'custom';

export interface FieldSpec {
  key: string;
  label: string;
  type: 'text' | 'password' | 'textarea' | 'boolean';
  required?: boolean;
  placeholder?: string;
  help?: string;
}

export interface ProviderInfo {
  id: ProviderId;
  label: string;
  blurb: string;
  docsUrl: string;
  authMode: AuthMode;
  configFields: FieldSpec[];
  credentialFields: FieldSpec[];
  supportsCreate: boolean;
}

/** Where a new task could be created upstream — e.g. a Monday board. */
export interface CreateTarget {
  id: string;
  label: string;
}

export interface Connection {
  id: string;
  name: string;
  provider: ProviderId;
  config: Record<string, string>;
  enabled: boolean;
  lastSyncAt: string | null;
  lastSyncStatus: 'ok' | 'error' | null;
  lastSyncError: string | null;
  createdAt: string;
  updatedAt: string;
  credentialPreview: Record<string, string>;
}

export interface FactorContribution {
  key: FactorKey;
  value: number;
  weight: number;
  points: number;
  reason: string | null;
}

export interface ScoreBreakdown {
  score: number;
  factors: FactorContribution[];
  adjustments: Array<{ label: string; multiplier: number }>;
  pinned: boolean;
  snoozed: boolean;
}

export interface TaskLink {
  id: string;
  type: LinkType;
  direction: 'outgoing' | 'incoming';
  otherTaskId: string;
  otherTitle: string;
  otherStatus: Status;
  otherSystem: string;
  /** When true, editing title/description/status on either task pushes the change to the other. */
  syncEnabled: boolean;
}

export interface Task {
  id: string;
  connectionId: string | null;
  connectionName: string | null;
  provider: ProviderId | 'manual';
  systemLabel: string;
  externalId: string | null;
  externalUrl: string | null;
  title: string;
  description: string | null;
  dueDate: string | null;
  status: Status;
  sourceStatus: string | null;
  priority: number | null;
  assignee: string | null;
  itemType: string | null;
  archived: boolean;
  removedByUser: boolean;
  createdAt: string;
  updatedAt: string;
  lastSyncedAt: string | null;

  importance: Level | null;
  importanceSource: ValueSource | null;
  urgency: Level;
  urgencyOverride: Level | null;
  effort: Effort | null;
  effortSource: ValueSource | null;
  impactType: ImpactType | null;
  impactScope: Level | null;
  impactSource: ValueSource | null;
  blockingOthers: boolean;
  blockingNote: string | null;
  commitmentTo: string | null;
  commitmentMadeAt: string | null;
  commitmentDue: string | null;
  projectId: string | null;
  projectName: string | null;
  orgPriorityId: string | null;
  orgPriorityName: string | null;
  sourceCreatedAt: string | null;
  firstSeenAt: string | null;
  lastTouchedAt: string | null;
  ageDays: number;
  pinned: boolean;
  snoozedUntil: string | null;
  completedAt: string | null;

  links: TaskLink[];
  artifacts: TaskArtifact[];
  needsTriage: boolean;
  score: ScoreBreakdown;
  nudges: string[];
}

/* ------------------------------------------------------------------ actions */

export type ActionProviderId = 'demo_docs' | 'google_docs';
export type AuthMode = 'form' | 'oauth';

export interface ActionProviderInfo {
  id: ActionProviderId;
  label: string;
  blurb: string;
  docsUrl: string;
  authMode: AuthMode;
  configFields: FieldSpec[];
  credentialFields: FieldSpec[];
}

export interface ActionConnection {
  id: string;
  provider: ActionProviderId;
  name: string;
  config: Record<string, string>;
  enabled: boolean;
  connected: boolean;
  createdAt: string;
  updatedAt: string;
  credentialPreview: Record<string, string>;
}

export interface TaskArtifact {
  id: string;
  provider: ActionProviderId;
  providerLabel: string;
  title: string;
  url: string;
  createdAt: string;
}

export interface TaskCounts {
  total: number;
  open: number;
  overdue: number;
  manual: number;
  needsTriage: number;
  promised: number;
  blocking: number;
}

export interface SyncReport {
  connectionId: string;
  connectionName: string;
  ok: boolean;
  created: number;
  updated: number;
  archived: number;
  fetched: number;
  derived: number;
  error?: string;
}

export interface TestResult {
  ok: boolean;
  message: string;
  details?: Record<string, unknown>;
}

export interface Project {
  id: string;
  name: string;
  color: string | null;
  orgPriorityId: string | null;
  orgPriorityName: string | null;
  taskCount: number;
  createdAt: string;
}

export interface OrgPriority {
  id: string;
  name: string;
  detail: string | null;
  weight: number;
  active: boolean;
  projectCount: number;
  createdAt: string;
}

export interface Profile {
  role: RolePreset;
  weights: Record<FactorKey, number>;
  agePressureDays: number;
}

export interface OnboardingState {
  completed: boolean;
}

export interface Insight {
  label: string;
  detail: string;
}

export interface Meta {
  statuses: Array<{ id: Status; label: string }>;
  importance: Array<{ id: number; label: string }>;
  urgency: Array<{ id: number; label: string }>;
  scope: Array<{ id: number; label: string }>;
  efforts: Array<{ id: Effort; label: string }>;
  impactTypes: Array<{ id: ImpactType; label: string }>;
  linkTypes: Array<{ id: LinkType; label: string }>;
  factors: Array<{ id: FactorKey; label: string; blurb: string }>;
  roles: Array<{ id: RolePreset; label: string; weights: Record<FactorKey, number> | null }>;
}

export type SortKey = 'priority' | 'title' | 'system' | 'dueDate' | 'status' | 'age' | 'updatedAt';
export type SortDir = 'asc' | 'desc';

/* ------------------------------------------------------------------- billing */

export type SubscriptionStatus = 'trialing' | 'active' | 'past_due' | 'canceled' | 'incomplete' | null;
export type Plan = 'monthly' | 'annual';

export interface PlanInfo {
  plan: Plan;
  amount: number;
  currency: string;
  interval: string;
}

export interface BillingStatus {
  status: SubscriptionStatus;
  plan: Plan | null;
  trialEndsAt: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  hasAccess: boolean;
  daysLeftInTrial: number | null;
}

/* ---------------------------------------------------------------------- auth */

export interface NotificationPrefs {
  enabled: boolean;
  dueSoon: boolean;
  dueSoonHours: number;
  overdue: boolean;
}

export interface AuthUser {
  id: string;
  email: string;
  createdAt: string;
  name: string | null;
  avatarUrl: string | null;
  hasGoogleLogin: boolean;
  isAdmin: boolean;
  emailVerified: boolean;
  notifications: NotificationPrefs;
  impersonating: { adminEmail: string } | null;
  billing: BillingStatus;
}

/* --------------------------------------------------------------------- admin */

export interface AdminUser {
  id: string;
  email: string;
  name: string | null;
  createdAt: string;
  isAdmin: boolean;
  emailVerified: boolean;
  hasGoogleLogin: boolean;
  subscriptionStatus: SubscriptionStatus;
  subscriptionPlan: Plan | null;
  trialEndsAt: string | null;
  taskCount: number;
  connectionCount: number;
}

export type SettingsSource = 'database' | 'environment' | 'none';

export interface StripeAdminSettings {
  secretKey: string;
  webhookSecret: string;
  priceMonthly: string;
  priceAnnual: string;
  configured: boolean;
  source: SettingsSource;
}

export interface MailAdminSettings {
  host: string;
  port: number;
  secure: boolean;
  user: string;
  pass: string;
  fromAddress: string;
  fromName: string;
  configured: boolean;
  source: SettingsSource;
}

export interface AuditLogEntry {
  id: string;
  adminEmail: string;
  targetEmail: string | null;
  action: string;
  detail: string | null;
  createdAt: string;
}

export interface Transaction {
  id: string;
  amount: number;
  amountRefunded: number;
  currency: string;
  status: string;
  refunded: boolean;
  description: string | null;
  created: string;
  receiptUrl: string | null;
}

export type RefundReason = 'duplicate' | 'fraudulent' | 'requested_by_customer';
